import * as React from 'react';
import { IAutoCompleteSearchFieldComponent } from '../components/autocomplete/AutoCompleteSearchField';

interface UseAddNewTagLogicOptions {
	/**
	 * Callback invoked when a new tag is selected from the "Add New Tag" footer
	 */
	onTagSelected: (tagName: string) => void;
}

interface UseAddNewTagLogicReturn {
	/**
	 * Callback to handle adding a new tag
	 */
	handleAddNewTag: (tagName: string) => void;

	/**
	 * Callback to pass to SimpleAutoCompleteSearchField's onSearchFieldRef prop
	 */
	onTagSearchFieldRef: (ref?: IAutoCompleteSearchFieldComponent) => void;

	/**
	 * Ref to the autocomplete search field component
	 */
	tagSearchFieldRef: React.RefObject<IAutoCompleteSearchFieldComponent>;
}

/**
 * Custom hook for managing "Add New Tag" logic in autocomplete components.
 * Handles ref management and tag selection coordination.
 *
 * @example
 * const { handleAddNewTag, onTagSearchFieldRef, tagSearchFieldRef } = useAddNewTagLogic({
 *   onTagSelected: (tagName) => setSelectedTag(tagName)
 * });
 *
 * const onRenderResultsFooter = useCallback((searchQuery, items, totalCount) => {
 *   if (totalCount === 0 && searchQuery) {
 *     return <AddNewTagFooter searchQuery={searchQuery} onAddTag={handleAddNewTag} />;
 *   }
 *   return null;
 * }, [handleAddNewTag]);
 *
 * <SimpleAutoCompleteSearchField
 *   onRenderResultsFooter={onRenderResultsFooter}
 *   onSearchFieldRef={onTagSearchFieldRef}
 *   // ... other props
 * />
 */
export function useAddNewTagLogic(options: UseAddNewTagLogicOptions): UseAddNewTagLogicReturn {
	const { onTagSelected } = options;
	const tagSearchFieldRef = React.useRef<IAutoCompleteSearchFieldComponent>(null);

	const handleAddNewTag = React.useCallback(
		(tagName: string) => {
			// Update the search field to show the selected tag
			if (tagSearchFieldRef.current) {
				tagSearchFieldRef.current.setSearchQuery(tagName);
			}
			// Notify parent component
			onTagSelected(tagName);
		},
		[onTagSelected]
	);

	const onTagSearchFieldRef = React.useCallback((ref?: IAutoCompleteSearchFieldComponent) => {
		tagSearchFieldRef.current = ref;
	}, []);

	return {
		handleAddNewTag,
		onTagSearchFieldRef,
		tagSearchFieldRef,
	};
}
